import { Columns3, Download, Rows3, Search, SlidersHorizontal } from 'lucide-react';
import type { Table } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import type { TablePrefs } from './use-table-prefs';

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  searchPlaceholder?: string;
  searchColumnId?: string;
  disableSearch?: boolean;
  /** Replaces the search box slot with custom content. */
  leftContent?: React.ReactNode;
  toolbar?: React.ReactNode;
  density: NonNullable<TablePrefs['density']>;
  onDensityChange: (next: NonNullable<TablePrefs['density']>) => void;
  onExport?: () => void;
  /** Export button text. */
  exportLabel?: string;
  /** Current page size — drives the page-size select at the top right. */
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  /** Allowed page-size options in the dropdown. */
  pageSizeOptions: number[];
}

export function DataTableToolbar<TData>({
  table,
  searchPlaceholder = 'Search…',
  searchColumnId,
  disableSearch = false,
  leftContent,
  toolbar,
  density,
  onDensityChange,
  onExport,
  exportLabel = 'Export',
  pageSize,
  onPageSizeChange,
  pageSizeOptions,
}: DataTableToolbarProps<TData>) {
  const globalFilter = table.getState().globalFilter as string | undefined;
  const columnFilter = searchColumnId
    ? ((table.getColumn(searchColumnId)?.getFilterValue() as string) ?? '')
    : '';

  const onSearch = (value: string) => {
    if (searchColumnId) {
      table.getColumn(searchColumnId)?.setFilterValue(value);
    } else {
      table.setGlobalFilter(value);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      {leftContent ? (
        leftContent
      ) : disableSearch ? (
        <div />
      ) : (
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={searchColumnId ? columnFilter : (globalFilter ?? '')}
            onChange={(e) => onSearch(e.target.value)}
            // Tailwind `!` modifier compiles to CSS `!important`, which
            // beats bootstrap.min.css's global input reset (loaded from
            // main.tsx) that was silently winning specificity + overriding
            // any regular padding-left declaration. Belt + braces: keep
            // the inline style too.
            className="!pl-11"
            style={{ paddingLeft: '2.75rem' }}
          />
        </div>
      )}

      <div className="flex items-center gap-2">
        {toolbar}

        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="hidden sm:inline">Show</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Rows per page"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
        </label>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Rows3 className="h-4 w-4" />
              <span className="hidden sm:inline">Density</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="z-[1100]">
            <DropdownMenuLabel>Row density</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={density}
              onValueChange={(v) => onDensityChange(v as NonNullable<TablePrefs['density']>)}
            >
              <DropdownMenuRadioItem value="compact">Compact</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="comfortable">Comfortable</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="spacious">Spacious</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Columns3 className="h-4 w-4" />
              <span className="hidden sm:inline">Columns</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="z-[1100] flex w-56 flex-col overflow-hidden p-0"
            style={{
              maxHeight: 'var(--radix-dropdown-menu-content-available-height, 70vh)',
            }}
          >
            <DropdownMenuLabel className="shrink-0">Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator className="shrink-0" />
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1">
              {table
                .getAllLeafColumns()
                .filter((c) => c.getCanHide())
                .map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.getIsVisible()}
                    onCheckedChange={(v) => column.toggleVisibility(Boolean(v))}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {readableColumnId(column.id)}
                  </DropdownMenuCheckboxItem>
                ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {onExport && (
          <Button variant="outline" size="sm" className="gap-2" onClick={onExport}>
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">{exportLabel}</span>
          </Button>
        )}

        <Button variant="ghost" size="sm" className="gap-2 lg:hidden" onClick={() => table.resetColumnFilters()}>
          <SlidersHorizontal className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function readableColumnId(id: string): string {
  return id
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}
